import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MaskService } from '../../../../../shared/services/mask.service';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import {
  ConfirmarPlanilhaRequest,
  ImportacaoInventarioItemConfirmar,
  ImportacaoInventarioItemValidacao,
  ImportacaoInventarioValidacaoResponse,
  InventarioImportacaoService,
} from '../../../../../shared/services/apis/orquestrador-suprimentos/inventario-importacao.service';

export interface ImportarPlanilhaPreviewData {
  validacao: ImportacaoInventarioValidacaoResponse;
  contexto: {
    idLocalizacaoEstoqueDefault: number;
    nomeLocalizacaoDefault: string;
    modoProcessamento?: number;
  };
  isEditarQuantidadeHabilitado: boolean;
}

export type ImportarPreviewResult =
  | { acao: 'confirmado'; idInventario: number }
  | { acao: 'escolherOutroArquivo' };

type FiltroStatus = 'todos' | 'validos' | 'avisos' | 'erros';

@Component({
  selector: 'app-importar-planilha-preview-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    NgxCurrencyDirective,
  ],
  templateUrl: './importar-planilha-preview-dialog.component.html',
  styleUrls: ['./importar-planilha-preview-dialog.component.css'],
})
export class ImportarPlanilhaPreviewDialogComponent implements OnInit {

  public formCabecalho: FormGroup;
  public filtroStatus: FiltroStatus = 'todos';
  public isConfirmando = false;

  public colunasExibidas: string[] = [
    'status',
    'numeroLinha',
    'codigoSku',
    'gtinEan',
    'nomeProduto',
    'nomeLocalizacao',
    'saldoSistema',
    'saldoContado',
    'ajuste',
  ];

  constructor(
    public dialogRef: MatDialogRef<ImportarPlanilhaPreviewDialogComponent, ImportarPreviewResult>,
    @Inject(MAT_DIALOG_DATA) public data: ImportarPlanilhaPreviewData,
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private importacaoService: InventarioImportacaoService,
    private maskService: MaskService,
  ) {}

  // Máscara de quantidade com as casas decimais globais do usuário (token info).
  public quantidadeMask = this.maskService.getDynamicDecimalMask();

  ngOnInit(): void {
    const descricaoAuto = this.gerarDescricaoAutomatica();
    this.formCabecalho = this.formBuilder.group({
      descricao: [descricaoAuto, Validators.required],
      observacao: [''],
    });
  }

  private gerarDescricaoAutomatica(): string {
    const meses = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
    ];
    const agora = new Date();
    return `Inventário Planilha - ${meses[agora.getMonth()]} ${agora.getFullYear()}`;
  }

  public get totalGeral(): number {
    return this.data.validacao.itens?.length ?? 0;
  }

  public get itensFiltrados(): ImportacaoInventarioItemValidacao[] {
    const itens = this.data.validacao.itens ?? [];
    switch (this.filtroStatus) {
      case 'validos': return itens.filter(i => i.status === 0);
      case 'avisos': return itens.filter(i => i.status === 1);
      case 'erros': return itens.filter(i => i.status === 2);
      default: return itens;
    }
  }

  public filtrar(status: FiltroStatus): void {
    this.filtroStatus = status;
  }

  public getDotClass(status: number): string {
    switch (status) {
      case 0: return 'dot-green';
      case 1: return 'dot-yellow';
      case 2: return 'dot-red';
      default: return 'dot-gray';
    }
  }

  public getRowClass(item: ImportacaoInventarioItemValidacao): string {
    if (item.status === 2) return 'row-error';
    if (item.status === 1) return 'row-warning';
    return '';
  }

  // Aplicado em todas as células da linha (não só na coluna de status) — hover em qualquer
  // parte da linha mostra o motivo, igual ao padrão do dialog-importar-produtos.component.html.
  public getTooltip(item: ImportacaoInventarioItemValidacao): string {
    return item.mensagem ?? '';
  }

  public ajusteClass(ajuste: number): string {
    if (ajuste > 0) return 'ajuste-positivo';
    if (ajuste < 0) return 'ajuste-negativo';
    return '';
  }

  public formatarNumero(valor: number): string {
    return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  public onSaldoContadoChange(item: ImportacaoInventarioItemValidacao): void {
    const novo = Number(item.saldoContado) || 0;
    item.saldoContado = novo;
    item.ajuste = novo - item.saldoSistema;
  }

  public podeEditarQuantidade(item: ImportacaoInventarioItemValidacao): boolean {
    return this.data.isEditarQuantidadeHabilitado && item.status !== 2;
  }

  public escolherOutroArquivo(): void {
    this.dialogRef.close({ acao: 'escolherOutroArquivo' });
  }

  public confirmarEGravar(): void {
    if (this.formCabecalho.invalid) {
      this.formCabecalho.markAllAsTouched();
      return;
    }

    const itensSemErro = (this.data.validacao.itens ?? []).filter(i => i.status !== 2);
    const itens: ImportacaoInventarioItemConfirmar[] = itensSemErro.map(i => ({
      idProduto: i.idProduto!,
      nomeProduto: i.nomeProduto!,
      codigoSku: i.codigoSku,
      gtinEan: i.gtinEan,
      idLocalizacaoEstoque: i.idLocalizacaoEstoque,
      nomeLocalizacao: i.nomeLocalizacao,
      saldoSistema: i.saldoSistema,
      saldoContado: i.saldoContado,
      custoMedio: i.custoMedio,
      isControlaLotes: i.isControlaLotes,
      isControlaSeries: i.isControlaSeries,
    }));

    const request: ConfirmarPlanilhaRequest = {
      descricao: this.formCabecalho.get('descricao')?.value,
      observacao: this.formCabecalho.get('observacao')?.value || undefined,
      idLocalizacaoEstoqueDefault: this.data.contexto.idLocalizacaoEstoqueDefault,
      nomeLocalizacaoDefault: this.data.contexto.nomeLocalizacaoDefault,
      modoProcessamento: this.data.contexto.modoProcessamento ?? 0,
      itens,
    };

    this.isConfirmando = true;
    this.importacaoService.confirmarPlanilha(request).subscribe({
      next: (res) => {
        this.isConfirmando = false;
        if (res.isError || !res.idInventario) {
          this.alertService.show('Erro ao confirmar', res.errorDescription ?? 'Erro desconhecido.', AlertType.error);
          return;
        }
        this.dialogRef.close({ acao: 'confirmado', idInventario: res.idInventario });
      },
      error: () => {
        this.isConfirmando = false;
        this.alertService.show('Erro', 'Falha ao confirmar a importação.', AlertType.error);
      }
    });
  }
}
